import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge, Button, Card, ConfirmDialog, EmptyState, LoadingList, SectionHeader, cx } from '@timely/ui';
import {
    AMOUNT_MODELS, DEFAULT_AMOUNT_MODEL, SHEET_FIELDS, amountModelDefinition, holds,
    isAmountModel, mappingProblems, modelProblems, parseSheetLink, sheetIdHint,
    sheetViewUrl, suggestAmountModel,
    type AmountModel, type ColumnMap, type IdentityStrategy, type SheetField, type SheetSource,
} from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { Field, FormNotice } from './AuthLayout';
import { apiMessage } from '../lib/api';
import {
    checkSheet, disconnect, runSync, saveConnection, useDataSource,
    type CheckResult, type SyncResult,
} from '../datasource/useDataSource';
import { previewGoogleSheet, selectGoogleSheet, useGoogleConnection } from '../datasource/google';
import { type ChosenGoogleSheet, GoogleAccountCard, GoogleSheetChooser } from '../datasource/GoogleConnect';

/**
 * Connecting a sheet, mapping its columns, and running a sync.
 *
 * The commercial path is Google (D22): connect the company's Google account,
 * choose the spreadsheet in Google's own file chooser, choose the exact tab,
 * map its columns, save. The sheet stays private — nobody needs a link to it —
 * and the server reads only the spreadsheet and numbered tab chosen, with the
 * company's own grant (08 §14).
 *
 * Nothing is written until the company confirms: choose, read, look at the
 * first rows exactly as they are, correct the guessed mapping, *then* save.
 * That ordering is the point — the guessed mapping is a suggestion, and the
 * single-tenant product's one silent mis-guess imported 3,991 staff email
 * addresses as customers' own.
 *
 * The "anyone with the link" connection is offered only in development builds,
 * for trying the product against a local stack without a Google client. A link
 * is parsed here for instant feedback and again on the server, which decides.
 */
export function DataSource({
    allowPublicLinks = import.meta.env.DEV,
}: {
    /**
     * Whether to offer the "anyone with the link" connection. Development
     * builds only: it is a way to try the product against a local stack
     * without a Google client, and never the commercial path (D22).
     */
    allowPublicLinks?: boolean;
} = {}) {
    const context = useActiveWorkspace();
    const workspaceId = context.workspace.id;
    const canManage = holds(context, 'datasource.manage');
    const canSync = holds(context, 'datasource.sync');

    const { integration, runs, loading, error, reload } = useDataSource(workspaceId);
    const google = useGoogleConnection(workspaceId);
    const [params, setParams] = useSearchParams();

    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
    const [busy, setBusy] = useState<'check' | 'save' | 'sync' | 'preview' | 'disconnect' | 'mapping' | null>(null);
    const [draft, setDraft] = useState<CheckResult | null>(null);
    const [draftTitle, setDraftTitle] = useState<string | null>(null);
    const [columns, setColumns] = useState<ColumnMap>({});
    const [strategy, setStrategy] = useState<IdentityStrategy>('source_key');
    const [model, setModel] = useState<AmountModel>(DEFAULT_AMOUNT_MODEL);
    const [lastSync, setLastSync] = useState<SyncResult | null>(null);
    const [confirmDisconnect, setConfirmDisconnect] = useState(false);
    const [choosing, setChoosing] = useState(false);

    const connected = integration.spreadsheetId !== null;
    const throughGoogle = integration.connectionType === 'api';

    // Back from Google. A first connection goes straight to choosing the
    // sheet; a reconnection keeps the sheet already chosen — asking again
    // would invite choosing a different one for no reason (found by the real
    // Google acceptance test, Session 18.1).
    const [returnedFromGoogle, setReturnedFromGoogle] = useState(false);
    useEffect(() => {
        if (params.get('google') !== 'connected') return;
        setReturnedFromGoogle(true);
        params.delete('google');
        setParams(params, { replace: true });
    }, [params, setParams]);
    useEffect(() => {
        if (!returnedFromGoogle || loading || google.loading) return;
        setReturnedFromGoogle(false);
        if (throughGoogle) {
            setNotice({ tone: 'ok', text: 'Google is connected again. Your sheet is still chosen — sync when you are ready.' });
        } else {
            setNotice({ tone: 'ok', text: 'Google is connected. Now choose your receivables sheet.' });
            setChoosing(true);
        }
    }, [returnedFromGoogle, loading, google.loading, throughGoogle]);

    const run = async (key: typeof busy, work: () => Promise<string | null>) => {
        // Guarded rather than merely disabled: a double submit is a second
        // sync, and the database refuses the second one, which is a worse way
        // to find out.
        if (busy) return;
        setBusy(key);
        setNotice(null);
        try {
            const message = await work();
            if (message) setNotice({ tone: 'ok', text: message });
        } catch (failed) {
            setNotice({ tone: 'error', text: failed instanceof Error ? failed.message : apiMessage(failed) });
        } finally {
            setBusy(null);
        }
    };

    const startMapping = (result: CheckResult, title: string | null, current?: ColumnMap) => {
        setDraft(result);
        setDraftTitle(title);
        setColumns(current && Object.keys(current).length ? current : result.suggested);
        setStrategy(current ? integration.identityStrategy : result.suggested.sourceKey ? 'source_key' : 'company_key');
        // Defensive against a deployment whose server predates this field:
        // fall back to reading the shape of the map rather than leaving the
        // choice blank.
        setModel(current ? integration.amountModel
            : isAmountModel(result.suggestedModel) ? result.suggestedModel : suggestAmountModel(result.suggested));
    };

    const fromGoogle = (chosen: ChosenGoogleSheet): CheckResult => ({
        source: { spreadsheetId: chosen.spreadsheetId, gid: String(chosen.tab.sheetId), connectionType: 'api' },
        headers: chosen.tab.headers,
        sample: chosen.tab.sample,
        rowCount: chosen.tab.rowCount,
        suggested: chosen.tab.suggested,
        suggestedModel: chosen.tab.suggestedModel,
        bytes: 0,
    });

    const refresh = async () => { await Promise.all([reload(), google.reload()]); };

    return (
        <Page>
            <SectionHeader
                title="Your receivables sheet"
                subtitle="Where the balances come from. Timely Payment reads it; it never writes to it."
                actions={connected && canSync && !draft ? (
                    <>
                        <Button
                            variant="secondary" disabled={busy !== null}
                            onClick={() => void run('preview', async () => {
                                const result = await runSync(workspaceId, true);
                                setLastSync(result);
                                await refresh();
                                return 'Checked. Nothing has been changed.';
                            })}
                        >
                            {busy === 'preview' ? 'Checking…' : 'Check without saving'}
                        </Button>
                        <Button
                            variant="primary" disabled={busy !== null}
                            onClick={() => void run('sync', async () => {
                                const result = await runSync(workspaceId, false);
                                setLastSync(result);
                                await refresh();
                                return summarise(result);
                            })}
                        >
                            {busy === 'sync' ? 'Syncing…' : 'Sync now'}
                        </Button>
                    </>
                ) : undefined}
            />

            {notice && <div className="mt-4"><FormNotice tone={notice.tone}>{notice.text}</FormNotice></div>}
            {(error || google.error) && <div className="mt-4"><FormNotice tone="error">{error ?? google.error}</FormNotice></div>}

            {loading || google.loading ? (
                <Card className="mt-5 overflow-hidden"><LoadingList label="Loading your data source" rows={3} /></Card>
            ) : (
                <>
                    <GoogleAccountCard
                        workspaceId={workspaceId}
                        slug={context.workspace.slug}
                        kind="receivables"
                        status={google.status}
                        canManage={canManage}
                        onChanged={() => void refresh()}
                    />

                    {draft ? (
                        <Mapping
                            draft={draft}
                            title={draftTitle}
                            columns={columns}
                            strategy={strategy}
                            model={model}
                            busy={busy === 'save'}
                            onColumns={setColumns}
                            onStrategy={setStrategy}
                            onModel={setModel}
                            onCancel={() => { setDraft(null); setNotice(null); }}
                            onSave={() => void run('save', async () => {
                                if (draft.source.connectionType === 'api') {
                                    await selectGoogleSheet(workspaceId, 'receivables', {
                                        spreadsheetId: draft.source.spreadsheetId,
                                        sheetId: Number(draft.source.gid),
                                        columns: columns as Record<string, string>,
                                        identityStrategy: strategy,
                                        amountModel: model,
                                    });
                                } else {
                                    await saveConnection(workspaceId, draft.source, columns, strategy, model, null);
                                }
                                setDraft(null);
                                await refresh();
                                return 'Connected. Run a sync when you are ready.';
                            })}
                        />
                    ) : choosing && google.status.connected && canManage ? (
                        <GoogleSheetChooser
                            workspaceId={workspaceId}
                            status={google.status}
                            onChosen={chosen => { setChoosing(false); startMapping(fromGoogle(chosen), chosen.tab.title); }}
                            onCancel={() => setChoosing(false)}
                        />
                    ) : connected ? (
                        <Connected
                            spreadsheetId={integration.spreadsheetId!}
                            gid={integration.sheetGid}
                            sheetName={integration.sheetName}
                            connectionType={integration.connectionType}
                            status={integration.status}
                            statusDetail={integration.statusDetail}
                            lastCheckedAt={integration.lastCheckedAt}
                            strategy={integration.identityStrategy}
                            model={integration.amountModel}
                            mappedCount={Object.keys(integration.columns).length}
                            canManage={canManage}
                            canChooseInGoogle={google.status.connected && google.status.connectedByMe}
                            busy={busy === 'mapping'}
                            onChangeSheet={() => setChoosing(true)}
                            onChangeMapping={() => void run('mapping', async () => {
                                if (!throughGoogle) { setDraft(null); return null; }
                                const preview = await previewGoogleSheet(workspaceId, integration.spreadsheetId!, Number(integration.sheetGid));
                                if (!preview.tab) throw new Error('Google did not return that tab. Choose the sheet again.');
                                startMapping(fromGoogle({ spreadsheetId: integration.spreadsheetId!, spreadsheetTitle: preview.spreadsheetTitle, tab: preview.tab }),
                                    preview.tab.title, integration.columns);
                                return null;
                            })}
                            onDisconnect={() => setConfirmDisconnect(true)}
                        />
                    ) : google.status.connected && canManage ? (
                        <GoogleSheetChooser
                            workspaceId={workspaceId}
                            status={google.status}
                            onChosen={chosen => startMapping(fromGoogle(chosen), chosen.tab.title)}
                        />
                    ) : !canManage ? (
                        <Card className="mt-5">
                            <EmptyState
                                title="No sheet is connected yet"
                                hint="Somebody with permission to manage the data source can connect one."
                            />
                        </Card>
                    ) : null}

                    {allowPublicLinks && canManage && !connected && !draft && (
                        <Card className="mt-5">
                            <Connect
                                busy={busy === 'check'}
                                onCheck={link => void run('check', async () => {
                                    const result = await checkSheet(workspaceId, link);
                                    startMapping(result, null);
                                    return null;
                                })}
                            />
                        </Card>
                    )}
                </>
            )}

            {lastSync && <SyncSummary result={lastSync} />}

            {runs.length > 0 && (
                <>
                    <div className="mt-9"><SectionHeader title="Recent syncs" /></div>
                    <Card className="mt-5 overflow-hidden">
                        <ul className="divide-y divide-separator">
                            {runs.map(r => (
                                <li key={r.id} className="px-5 py-3.5 flex items-start justify-between gap-4">
                                    <div className="min-w-0">
                                        <div className="text-[14px] text-label">
                                            {r.result === 'ok' ? 'Synced' : r.result === 'cancelled' ? 'Checked only' : r.finishedAt ? 'Failed' : 'Running'}
                                            {r.counts.read !== undefined && ` · ${r.counts.read} rows read`}
                                            {r.counts.changed ? ` · ${r.counts.changed} changed` : ''}
                                            {r.counts.new ? ` · ${r.counts.new} new` : ''}
                                            {r.counts.settled ? ` · ${r.counts.settled} settled` : ''}
                                        </div>
                                        {r.errorDetail && (
                                            <div className="text-[12.5px] text-dang mt-1">{r.errorDetail}</div>
                                        )}
                                    </div>
                                    <time dateTime={r.startedAt} className="text-[12.5px] text-label-3 num flex-none">
                                        {new Date(r.startedAt).toISOString().replace('T', ' ').slice(0, 16)}
                                    </time>
                                </li>
                            ))}
                        </ul>
                    </Card>
                </>
            )}

            <ConfirmDialog
                open={confirmDisconnect}
                title="Remove this sheet?"
                confirmLabel="Remove"
                busy={busy === 'disconnect'}
                onCancel={() => setConfirmDisconnect(false)}
                onConfirm={() => {
                    setConfirmDisconnect(false);
                    void run('disconnect', async () => {
                        await disconnect(workspaceId);
                        await refresh();
                        return 'Removed. Your accounts and their history are untouched.';
                    });
                }}
            >
                <p>
                    Your customers, their follow-ups, notes and cheques all stay exactly as they are.
                    Only the connection to this sheet goes, and syncing stops until a sheet is chosen again.
                </p>
            </ConfirmDialog>
        </Page>
    );
}

/* -------------------------------------------------------------------------- */

function Connect({ busy, onCheck }: { busy: boolean; onCheck: (link: string) => void }) {
    const [link, setLink] = useState('');
    const [problem, setProblem] = useState<string | null>(null);

    return (
        <div className="px-5 py-6">
            <h3 className="text-[16px] font-bold text-label tracking-[-0.02em]">A published link — for local testing only</h3>
            <p className="text-[13.5px] text-label-2 mt-2 mb-5 leading-relaxed max-w-prose">
                This build is a development build, so a sheet shared with <strong>Anyone with the link</strong>
                {' '}can be read without a Google connection. A deployed Timely Payment never offers this:
                a link anybody can open exposes the whole book to whoever it is forwarded to.
            </p>

            <form
                noValidate
                className="flex items-end gap-3 flex-wrap"
                onSubmit={e => {
                    e.preventDefault();
                    // Parsed here for an instant answer. The server parses it
                    // again, and that is the parse that decides.
                    const parsed = parseSheetLink(link);
                    if (!parsed.ok) { setProblem(parsed.message); return; }
                    setProblem(null);
                    onCheck(link.trim());
                }}
            >
                <div className="flex-1 min-w-[260px]">
                    <Field
                        label="Google Sheets link" name="sheet-link" value={link}
                        autoComplete="off" error={problem}
                        onChange={e => setLink(e.target.value)}
                    />
                </div>
                <div className="mb-4">
                    <Button type="submit" variant="primary" disabled={busy}>
                        {busy ? 'Reading…' : 'Read this sheet'}
                    </Button>
                </div>
            </form>
        </div>
    );
}

function Mapping({
    draft, title, columns, strategy, model, busy, onColumns, onStrategy, onModel, onCancel, onSave,
}: {
    draft: CheckResult;
    /** The tab's name, when it was chosen through Google. */
    title?: string | null;
    columns: ColumnMap;
    strategy: IdentityStrategy;
    model: AmountModel;
    busy: boolean;
    onColumns: (map: ColumnMap) => void;
    onStrategy: (strategy: IdentityStrategy) => void;
    onModel: (model: AmountModel) => void;
    onCancel: () => void;
    onSave: () => void;
}) {
    // Both kinds of problem block a save. A column that the chosen model never
    // reads is as much a mistake as a column that is missing: it means the
    // company believes something about their import that is not true.
    const problems = [
        ...mappingProblems(draft.headers, columns, model),
        ...modelProblems(model, columns),
    ];
    const blocking = problems.filter(p => p.kind !== 'unknown-header');

    const set = (field: SheetField, header: string) => {
        const next = { ...columns };
        if (header === '') delete next[field]; else next[field] = header;
        onColumns(next);
    };

    return (
        <>
            <Card className="mt-5 px-5 py-5">
                <h3 className="text-[16px] font-bold text-label tracking-[-0.02em]">
                    We read {draft.rowCount} {draft.rowCount === 1 ? 'row' : 'rows'}
                    {title ? ` from “${title}”` : ''}
                </h3>
                <p className="text-[13.5px] text-label-3 mt-1 mb-4">
                    Here are the first few, exactly as they are in your sheet.
                </p>

                <div className="overflow-x-auto -mx-5 px-5">
                    <table className="text-[12.5px] border-collapse">
                        <thead>
                            <tr>
                                {draft.headers.map((h, i) => (
                                    <th key={i} className="text-left font-semibold text-label-2 border-b border-separator px-2 py-1.5 whitespace-nowrap">
                                        {h || <span className="text-label-3">(blank)</span>}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {draft.sample.slice(0, 8).map((r, i) => (
                                <tr key={i}>
                                    {draft.headers.map((_, c) => (
                                        <td key={c} className="border-b border-separator px-2 py-1.5 whitespace-nowrap text-label-2">
                                            {r[c] ?? ''}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Card className="mt-5 px-5 py-5">
                <h3 className="text-[16px] font-bold text-label tracking-[-0.02em]">Which column is which</h3>
                <p className="text-[13.5px] text-label-3 mt-1 mb-4 leading-relaxed max-w-prose">
                    We have guessed. Check it — a wrong guess here imports the wrong data everywhere,
                    and it is much harder to notice afterwards.
                </p>

                {blocking.length > 0 && (
                    <FormNotice tone="error">
                        {blocking.map(p => p.message).join(' ')}
                    </FormNotice>
                )}

                <div className="grid gap-x-5 sm:grid-cols-2">
                    {SHEET_FIELDS.map(definition => (
                        <div key={definition.field} className="mb-3.5">
                            <label
                                htmlFor={`map-${definition.field}`}
                                className="block text-[13px] font-semibold text-label mb-1"
                            >
                                {definition.label}
                                {definition.requirement === 'required' && <span className="text-dang"> *</span>}
                                {definition.requirement === 'recommended' && (
                                    <span className="text-label-3 font-normal"> · recommended</span>
                                )}
                            </label>
                            <select
                                id={`map-${definition.field}`}
                                value={columns[definition.field] ?? ''}
                                onChange={e => set(definition.field, e.target.value)}
                                className={cx(
                                    'w-full h-10 px-3 rounded-[10px] bg-card border text-[13.5px] text-label outline-none focus:border-accent',
                                    definition.requirement === 'required' && !columns[definition.field]
                                        ? 'border-dang' : 'border-separator-strong',
                                )}
                            >
                                <option value="">— not in my sheet —</option>
                                {draft.headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
                            </select>
                            {!columns[definition.field] && definition.lostWithout && (
                                <div className="text-[12px] text-label-3 mt-1 leading-snug">
                                    {definition.lostWithout}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <div className="mt-2 pt-4 border-t border-separator">
                    <div className="text-[13px] font-semibold text-label mb-1">
                        How does your sheet say which way an amount points?
                    </div>
                    <p className="text-[12px] text-label-3 mb-2.5 leading-snug max-w-prose">
                        Every accounting export writes this differently, and we will not guess.
                        If a row disagrees with itself, we will leave that account exactly as it
                        is and tell you the row number, rather than pick one and carry on.
                    </p>
                    {AMOUNT_MODELS.map(definition => (
                        <label key={definition.model} className="flex items-start gap-2.5 mb-2">
                            <input
                                type="radio" name="amount-model" className="mt-1"
                                checked={model === definition.model}
                                onChange={() => onModel(definition.model)}
                            />
                            <span className="text-[13.5px] text-label leading-snug">
                                {definition.label}
                                <span className="block text-[12px] text-label-3">{definition.hint}</span>
                            </span>
                        </label>
                    ))}
                </div>

                <div className="mt-2 pt-4 border-t border-separator">
                    <div className="text-[13px] font-semibold text-label mb-2">
                        How should we recognise the same customer next time?
                    </div>
                    <label className="flex items-start gap-2.5 mb-2">
                        <input
                            type="radio" name="strategy" className="mt-1"
                            checked={strategy === 'source_key'}
                            disabled={!columns.sourceKey}
                            onChange={() => onStrategy('source_key')}
                        />
                        <span className="text-[13.5px] text-label leading-snug">
                            By customer code
                            <span className="block text-[12px] text-label-3">
                                The safe choice. A customer keeps their account even if you rename them.
                                {!columns.sourceKey && ' Choose a column for Customer code above to use this.'}
                            </span>
                        </span>
                    </label>
                    <label className="flex items-start gap-2.5">
                        <input
                            type="radio" name="strategy" className="mt-1"
                            checked={strategy === 'company_key'}
                            onChange={() => onStrategy('company_key')}
                        />
                        <span className="text-[13.5px] text-label leading-snug">
                            By customer name
                            <span className="block text-[12px] text-label-3">
                                Use this only if your sheet has no code column. Two customers with the
                                same name become one account, and the sync will list them before it does.
                            </span>
                        </span>
                    </label>
                </div>

                <div className="mt-6 flex items-center gap-2">
                    <Button variant="primary" disabled={busy || blocking.length > 0} onClick={onSave}>
                        {busy ? 'Saving…' : 'Save this connection'}
                    </Button>
                    <Button variant="quiet" disabled={busy} onClick={onCancel}>Cancel</Button>
                </div>
            </Card>
        </>
    );
}

function Connected({
    spreadsheetId, gid, sheetName, connectionType, status, statusDetail, lastCheckedAt,
    strategy, model, mappedCount, canManage, canChooseInGoogle, busy, onChangeSheet, onChangeMapping, onDisconnect,
}: {
    spreadsheetId: string;
    gid: string | null;
    sheetName: string | null;
    connectionType: 'export_csv' | 'published_csv' | 'api';
    status: 'unconfigured' | 'ok' | 'error';
    statusDetail: string | null;
    lastCheckedAt: string | null;
    strategy: IdentityStrategy;
    model: AmountModel;
    mappedCount: number;
    canManage: boolean;
    /** Whether this person's Google account is the connected one. */
    canChooseInGoogle: boolean;
    busy: boolean;
    onChangeSheet: () => void;
    onChangeMapping: () => void;
    onDisconnect: () => void;
}) {
    const throughGoogle = connectionType === 'api';
    // The link to open the sheet in Google is shown only to somebody who may
    // manage the source (08 §6), and is built from the stored parts.
    let openInGoogle: string | null = null;
    if (canManage) {
        try {
            openInGoogle = sheetViewUrl({ spreadsheetId, gid, connectionType } as SheetSource);
        } catch {
            openInGoogle = null;   // a stored value the builder will not accept
        }
    }

    return (
        <Card className="mt-5 px-5 py-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[15px] font-semibold text-label">Google Sheet</span>
                        <Badge tone={status === 'ok' ? 'pos' : status === 'error' ? 'dang' : 'neutral'}>
                            {status === 'ok' ? 'Connected' : status === 'error' ? 'Needs attention' : 'Not set up'}
                        </Badge>
                        {connectionType === 'published_csv' && <Badge>Published</Badge>}
                        {!throughGoogle && <Badge tone="warn">Public link</Badge>}
                    </div>
                    <div className="text-[13px] text-label-3 mt-1 num">
                        Sheet {sheetIdHint(spreadsheetId)}
                        {throughGoogle && sheetName ? ` · tab “${sheetName}”` : gid !== null ? ` · tab ${gid}` : ''}
                        {` · ${mappedCount} columns mapped`}
                        {` · matched by ${strategy === 'source_key' ? 'customer code' : 'customer name'}`}
                    </div>
                    {/* Said plainly, because it decides what every balance in
                        the book means and it is not visible anywhere else. */}
                    <div className="text-[12.5px] text-label-3 mt-0.5">
                        Amounts read as: {amountModelDefinition(model)?.label ?? model}
                    </div>
                    {lastCheckedAt && (
                        <div className="text-[12.5px] text-label-3 mt-0.5">
                            Last checked <time dateTime={lastCheckedAt}>
                                {new Date(lastCheckedAt).toISOString().replace('T', ' ').slice(0, 16)}
                            </time>
                        </div>
                    )}
                </div>

                {canManage && (
                    <div className="flex items-center gap-2 flex-none">
                        {openInGoogle && (
                            <a
                                href={openInGoogle} target="_blank" rel="noreferrer noopener"
                                className="text-[13px] text-accent font-semibold hover:underline"
                            >
                                Open in Google
                            </a>
                        )}
                        {throughGoogle && canChooseInGoogle && (
                            <Button size="sm" variant="secondary" disabled={busy} onClick={onChangeSheet}>Change sheet</Button>
                        )}
                        {throughGoogle && canChooseInGoogle && (
                            <Button size="sm" variant="secondary" disabled={busy} onClick={onChangeMapping}>
                                {busy ? 'Reading…' : 'Change mapping'}
                            </Button>
                        )}
                        <Button size="sm" variant="quiet" disabled={busy} onClick={onDisconnect}>Remove sheet</Button>
                    </div>
                )}
            </div>

            {status === 'error' && statusDetail && (
                <div className="mt-4"><FormNotice tone="error">{statusDetail}</FormNotice></div>
            )}
            {!throughGoogle && (
                <div className="mt-4">
                    <FormNotice tone="error">
                        This sheet is read through a link anybody could open, so the whole book is exposed to
                        whoever it is forwarded to. Connect Google above and choose the sheet there, then turn off
                        its link sharing.
                    </FormNotice>
                </div>
            )}
        </Card>
    );
}

function SyncSummary({ result }: { result: SyncResult }) {
    const c = result.counts;
    return (
        <Card className="mt-5 px-5 py-5">
            <h3 className="text-[15px] font-bold text-label mb-1">
                {result.applied ? 'What this sync did' : 'What a sync would do'}
            </h3>
            {/* Honest counts, not "3,992 rows processed" when eleven figures
                moved. The settled amount is named because quietly writing off
                a book is the failure nobody notices until month end. */}
            <p className="text-[13.5px] text-label-2 leading-relaxed">
                Read {c.read}. {c.new} new, {c.changed} changed, {c.unchanged} unchanged,
                {' '}{c.settled} settled{c.settledAmount ? ` (${Math.round(c.settledAmount).toLocaleString()} cleared)` : ''}.
                {c.excluded ? ` ${c.excluded} excluded.` : ''}
            </p>

            {/* Said separately, and not folded into "excluded": these accounts
                are still in the sheet. We could not read what they say, so
                their figures were left exactly as they were rather than being
                written off. */}
            {c.heldBack ? (
                <p className="text-[13px] text-warn mt-2 leading-relaxed">
                    {c.heldBack} {c.heldBack === 1 ? 'account was' : 'accounts were'} left
                    {' '}untouched because {c.heldBack === 1 ? 'its row' : 'their rows'} could
                    {' '}not be read. {c.heldBack === 1 ? 'It keeps' : 'They keep'} the figures
                    {' '}{c.heldBack === 1 ? 'it' : 'they'} had.
                </p>
            ) : null}

            {result.excluded.length > 0 && (
                <details className="mt-3">
                    <summary className="text-[13px] font-semibold text-label cursor-pointer">
                        {result.excluded.length} rows were not imported
                    </summary>
                    <ul className="mt-2 space-y-1">
                        {result.excluded.map(e => (
                            <li key={e.rowNumber} className="text-[12.5px] text-label-3">
                                Row {e.rowNumber}: “{e.label}” — {e.reason === 'summary-row' ? 'a total, not a customer' : 'blank'}
                            </li>
                        ))}
                    </ul>
                </details>
            )}

            {result.problems.length > 0 && (
                <details className="mt-3" open>
                    <summary className="text-[13px] font-semibold text-dang cursor-pointer">
                        {result.problems.length} rows need attention
                    </summary>
                    <ul className="mt-2 space-y-1">
                        {result.problems.map((p, i) => (
                            <li key={`${p.rowNumber}-${i}`} className="text-[12.5px] text-label-2">{p.message}</li>
                        ))}
                    </ul>
                </details>
            )}
        </Card>
    );
}

const summarise = (result: SyncResult): string => {
    const c = result.counts;
    if (!c.new && !c.changed && !c.settled) return 'Nothing had changed since the last sync.';
    const parts: string[] = [];
    if (c.new) parts.push(`${c.new} new`);
    if (c.changed) parts.push(`${c.changed} updated`);
    if (c.settled) parts.push(`${c.settled} settled`);
    return `Synced: ${parts.join(', ')}.`;
};
